// src/pages/tools/ToolEditor.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  IconButton,
  Chip,
  // useTheme, // Not directly used here anymore, but sub-components might
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  // SmartToy as SmartToyIcon, // Removed as agent tab is disabled
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { toolService } from '../../api/toolService';
import { modelService } from '../../api/modelService'; // Still load models for potential future use or other parts

import ToolDetailsTab from '../../components/tools/ToolDetailsTab';
import ToolCodeTab from '../../components/tools/ToolCodeTab';
// import ToolAgentTab from './components/ToolAgentTab'; // Commented out

// Default tool template (can be moved to a constants/utils file)
const DEFAULT_TOOL_TEMPLATE = `async def tool_function(ctx: RunContext, **kwargs) -> dict:\n    """\n    Description of what the tool does.\n\n    Args:\n        ctx: The run context\n        **kwargs: The input parameters for the tool\n\n    Returns:\n        A dictionary with the results\n    """\n    # Implementation of the tool\n    # Example: return {"result": "Tool executed successfully"}\n    return {}`;

// Tool templates for different types (can be moved to a constants/utils file)
const TOOL_TEMPLATES = {
  search: `async def search_tool(ctx: RunContext, query: str) -> dict:\n    """\n    Search for information based on the provided query.\n\n    Args:\n        ctx: The run context.\n        query: The search query string.\n\n    Returns:\n        A dictionary containing the search results. \n        Example: {"results": [{"title": "Result 1", "snippet": "..."}]}\n    """\n    # Example: Search logic using httpx\n    # import httpx\n    # async with httpx.AsyncClient() as client:\n    #   response = await client.get(f"https://api.example.com/search?q={query}")\n    #   response.raise_for_status()\n    #   return response.json()\n    return {"message": f"Search results for: {query}"}`,
  api: `async def api_tool(ctx: RunContext, endpoint: str, method: str = "GET", params: dict = None, data: dict = None) -> dict:\n    """\n    Interact with an external API.\n\n    Args:\n        ctx: The run context.\n        endpoint: The API endpoint URL.\n        method: The HTTP method (GET, POST, PUT, DELETE, etc.). Defaults to "GET".\n        params: Query parameters for the request.\n        data: JSON body for POST/PUT requests.\n\n    Returns:\n        A dictionary with the API response.\n        Example: {"status_code": 200, "data": { ... }}\n    """\n    import httpx\n    import json\n    async with httpx.AsyncClient() as client:\n        try:\n            request_kwargs = {"params": params or {}}\n            if data:\n                request_kwargs["json"] = data\n            response = await client.request(method.upper(), endpoint, **request_kwargs)\n            response.raise_for_status()\n            try:\n                response_data = response.json()\n            except json.JSONDecodeError:\n                response_data = response.text\n            return {"status_code": response.status_code, "data": response_data}\n        except httpx.HTTPStatusError as e:\n            return {"error": f"HTTP error: {e.response.status_code}", "response": e.response.text}\n        except Exception as e:\n            return {"error": str(e)}`,
  calculation: `async def calculation_tool(ctx: RunContext, expression: str) -> dict:\n    """\n    Perform a calculation based on the input expression.\n\n    Args:\n        ctx: The run context.\n        expression: The mathematical or logical expression to evaluate.\n\n    Returns:\n        A dictionary with the calculation result.\n        Example: {"result": 25}\n    """\n    try:\n        # IMPORTANT: eval() is dangerous with untrusted input.
        # Consider safer alternatives like ast.literal_eval for simple literals,
        # or a dedicated math expression parser library.
        result = eval(expression, {"__builtins__": {}}, {}) \n        return {"result": result}\n    except Exception as e:\n        return {"error": f"Calculation error: {str(e)}"}"`,
  database: `async def database_tool(ctx: RunContext, query: str, query_type: str = "select") -> dict:\n    """\n    Interact with a database (conceptual example).\n\n    Args:\n        ctx: The run context.\n        query: The database query (e.g., SQL).\n        query_type: Type of query (e.g., "select", "insert", "update").\n\n    Returns:\n        A dictionary with the query results or status.\n    """\n    # This is a placeholder. Actual DB interaction needs a library and secure connection handling.
    if query_type == "select":\n        return {"results": [{"id": 1, "name": "Sample Row"}], "count": 1}\n    return {"message": f"Executed {query_type} query (simulated): {query}"}`,
  utility: `async def format_data_tool(ctx: RunContext, data: str, target_format: str = "json_pretty") -> dict:\n    """\n    Format or convert data between different formats.\n\n    Args:\n        ctx: The run context.\n        data: The input data string (e.g., a JSON string).\n        target_format: Target format (e.g., "json_pretty", "yaml").\n\n    Returns:\n        A dictionary with the formatted data or an error.\n    """\n    import json\n    # import yaml # if using yaml\n    try:\n        parsed_data = json.loads(data) # Assumes input is JSON\n        if target_format == "json_pretty":\n            return {"formatted_data": json.dumps(parsed_data, indent=2)}\n        # elif target_format == "yaml":\n        #     return {"formatted_data": yaml.dump(parsed_data)}\n        else:\n            return {"error": f"Unsupported format: {target_format}"}\n    except Exception as e:\n        return {"error": f"Data formatting error: {str(e)}"}"`,
  custom: DEFAULT_TOOL_TEMPLATE,
};

const generateToolSlug = (name) => {
  if (!name) return '';
  return name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '').replace(/_+/g, '_').replace(/^_|_$/g, '').substring(0, 50);
};

const initialToolState = {
  slug: '',
  name: '',
  description: '',
  tool_type: 'utility',
  enabled: true,
  code: TOOL_TEMPLATES['utility'],
  default_params: '{}',
  requires_dedicated_agent: false,
  dedicated_llm_model: '',
  dedicated_system_prompt: '',
  agent_config: { temperature: 0.4, max_tokens: 2000 },
};


const ToolEditor = () => {
  const { id: toolUuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditing = !!toolUuid;

  const [tool, setTool] = useState(initialToolState);
  const [initialToolCodeForReset, setInitialToolCodeForReset] = useState(TOOL_TEMPLATES['utility']);
  const [originalToolDataForCompare, setOriginalToolDataForCompare] = useState(null);
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [errors, setErrors] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [availableModels, setAvailableModels] = useState([]);
  const previousToolTypeRef = useRef(initialToolState.tool_type);


  useEffect(() => {
    const loadModels = async () => {
      const response = await modelService.getAvailableModels();
      if (response.isError) {
        setAvailableModels([]);
      } else {
        let modelsArray = [];
        if (Array.isArray(response)) {
          modelsArray = response.map(m => typeof m === 'string' ? { id: m, name: m } : m);
        } else if (typeof response === 'object' && response !== null) {
          Object.values(response).forEach(providerModels => {
            if (typeof providerModels === 'object' && providerModels !== null) {
              Object.entries(providerModels).forEach(([id, name]) => modelsArray.push({ id, name: String(name) }));
            }
          });
        }
        modelsArray.sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
        setAvailableModels(modelsArray);
      }
    };
    loadModels();
  }, []);

  useEffect(() => {
    const loadTool = async () => {
      if (isEditing && toolUuid) {
        setLoading(true);
        try {
          const toolDetailsRes = await toolService.getToolById(toolUuid);
          if (toolDetailsRes.isError) {
            toast.error(`Failed to load tool: ${toolDetailsRes.message}`);
            navigate('/tools'); return;
          }

          const codeRes = await toolService.getToolCode(toolUuid);
          let loadedCode = TOOL_TEMPLATES[toolDetailsRes.tool_type] || DEFAULT_TOOL_TEMPLATE;
          if (codeRes.isError) toast.warn(`Failed to load tool code, using template: ${codeRes.message}`);
          else loadedCode = codeRes.code || loadedCode;
          
          const loadedToolData = {
            ...initialToolState,
            ...toolDetailsRes,
            slug: toolDetailsRes.id,
            uuid: toolDetailsRes.id_, // Ensure your backend provides UUID as 'id_' or similar
            code: loadedCode,
            default_params: typeof toolDetailsRes.default_params === 'object' ? JSON.stringify(toolDetailsRes.default_params, null, 2) : (toolDetailsRes.default_params || '{}'),
          };
          setTool(loadedToolData);
          setInitialToolCodeForReset(loadedCode);
          setOriginalToolDataForCompare(JSON.parse(JSON.stringify(loadedToolData)));
          previousToolTypeRef.current = loadedToolData.tool_type; // Initialize previousToolTypeRef

          const urlParams = new URLSearchParams(location.search);
          if (urlParams.get('tab') === 'code') setTabValue(1);
          else setTabValue(0);

        } catch (err) {
          toast.error('An unexpected error occurred while loading tool data.');
          console.error(err); navigate('/tools');
        } finally {
          setLoading(false);
        }
      } else {
        const defaultCode = TOOL_TEMPLATES[initialToolState.tool_type];
        const newToolInitialData = {...initialToolState, code: defaultCode };
        setTool(newToolInitialData);
        setInitialToolCodeForReset(defaultCode);
        setOriginalToolDataForCompare(JSON.parse(JSON.stringify(newToolInitialData)));
        previousToolTypeRef.current = initialToolState.tool_type; // Initialize for new tool
        setLoading(false);
      }
    };
    loadTool();
  }, [toolUuid, isEditing, navigate, location.search]);

  useEffect(() => {
    if (!isEditing && tool.name && !tool.slug) {
      setTool(prev => ({ ...prev, slug: generateToolSlug(prev.name) }));
    }
  }, [tool.name, isEditing, tool.slug]);

  useEffect(() => {
    const currentToolType = tool.tool_type;
    const previousToolType = previousToolTypeRef.current;

    if (currentToolType !== previousToolType) {
      const newTemplateCode = TOOL_TEMPLATES[currentToolType] || DEFAULT_TOOL_TEMPLATE;
      const oldTemplateCodeForPreviousType = TOOL_TEMPLATES[previousToolType] || DEFAULT_TOOL_TEMPLATE;
      
      // Check if the code was the template for the *previous* type OR if it's a new tool and code is initial template
      const isCodeThePreviousTemplate = tool.code === oldTemplateCodeForPreviousType;
      const isNewToolAndCodeIsInitialDefault = !isEditing && tool.code === (TOOL_TEMPLATES[initialToolState.tool_type] || DEFAULT_TOOL_TEMPLATE);


      if (isCodeThePreviousTemplate || isNewToolAndCodeIsInitialDefault) {
        if (tool.code !== newTemplateCode) {
          setTool(prev => ({ ...prev, code: newTemplateCode }));
          if (!isEditing) {
            setInitialToolCodeForReset(newTemplateCode); // For new tools, the "reset" point also changes
          }
        }
      }
      previousToolTypeRef.current = currentToolType;
    }
  }, [tool.tool_type, tool.code, isEditing, initialToolState.tool_type]);


  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const handleChange = useCallback((e) => {
    const { name, value, checked, type } = e.target;
    setTool(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  }, [errors]);

  const handleCodeChange = useCallback((newCode) => {
    setTool(prev => ({ ...prev, code: newCode || '' }));
  }, []);

  const handleResetCode = useCallback(() => {
    if (window.confirm('Are you sure you want to reset the code to its last saved state (or the current type template for new, unsaved tools)?')) {
      setTool(prev => ({ ...prev, code: initialToolCodeForReset }));
    }
  }, [initialToolCodeForReset]);

  const validateForm = () => {
    const newErrors = {};
    if (!tool.slug && !isEditing) newErrors.slug = 'Tool Slug/ID is required';
    else if (!isEditing && tool.slug && !/^[a-z0-9_]+(-[a-z0-9_]+)*$/.test(tool.slug)) newErrors.slug = 'Slug must be lowercase, numbers, underscores, or hyphens.';
    if (!tool.name) newErrors.name = 'Tool name is required';
    if (!tool.description) newErrors.description = 'Description is required';
    
    try {
      if (tool.default_params && tool.default_params.trim() !== '') JSON.parse(tool.default_params);
    } catch (e) { newErrors.default_params = 'Default Parameters: Invalid JSON format'; }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fix the errors before saving.");
      return;
    }
    setSaving(true);
    try {
      const toolDataToSubmit = {
        ...tool,
        id: tool.slug, 
        default_params: JSON.parse(tool.default_params || '{}'),
        // Agent fields will be sent based on current `tool` state.
        // Backend should handle them if `requires_dedicated_agent` is false.
      };
      if (!isEditing) delete toolDataToSubmit.uuid; // Remove uuid if present from initial state for create

      let mainResponse;
      if (isEditing) {
        mainResponse = await toolService.updateTool(toolUuid, toolDataToSubmit); 
        if (!mainResponse.isError && tool.code !== initialToolCodeForReset) { // Only update code if it changed
          const codeUpdateResponse = await toolService.updateToolCode(toolUuid, tool.code);
          if (codeUpdateResponse.isError) {
            toast.error(`Tool metadata updated, but failed to update code: ${codeUpdateResponse.message}`);
          } else {
            setInitialToolCodeForReset(tool.code); // Update reset baseline after successful code save
          }
        } else if (!mainResponse.isError) {
            // If metadata updated successfully but code didn't need update,
            // still update the 'originalToolDataForCompare' and 'initialToolCodeForReset'
             setOriginalToolDataForCompare(JSON.parse(JSON.stringify({...tool, code: tool.code})));
             setInitialToolCodeForReset(tool.code); // Ensure this is also up-to-date
        }
      } else {
        mainResponse = await toolService.createTool(toolDataToSubmit);
      }
      
      if (mainResponse.isError) {
        toast.error(`Failed to ${isEditing ? 'update' : 'create'} tool: ${mainResponse.message}`);
      } else {
        toast.success(`Tool ${isEditing ? 'updated' : 'created'} successfully!`);
        const newToolData = mainResponse; // This is the response from create/update metadata
        
        // Update original data for dirty checking
        const updatedFullToolData = {...tool, code: tool.code};
        if (isEditing && newToolData && !newToolData.isError) { // update metadata from response
            updatedFullToolData.name = newToolData.name || tool.name;
            updatedFullToolData.description = newToolData.description || tool.description;
            // ... update other fields that backend might modify/return on PUT
        }
        setOriginalToolDataForCompare(JSON.parse(JSON.stringify(updatedFullToolData)));
        
        if (!isEditing) { // After successful creation
            const newToolUuidFromResponse = newToolData.id_ || newToolData.uuid; // Adjust field name based on your backend
            if (newToolUuidFromResponse) {
                navigate(`/tools/${newToolUuidFromResponse}/edit`, { replace: true });
            } else {
                console.warn("New tool UUID not found directly in POST response, attempting refetch by slug.");
                // This part is a fallback and might be slow or unreliable if slugs are not unique across users.
                // Best if backend POST response includes the UUID.
                const allTools = await toolService.getAllTools();
                const found = Array.isArray(allTools) ? allTools.find(t => t.id === tool.slug) : null;
                if (found && (found.id_ || found.uuid)) {
                    navigate(`/tools/${found.id_ || found.uuid}/edit`, { replace: true });
                } else {
                    toast.info("Tool created, but could not navigate to edit page. Please find it in the list.");
                    navigate('/tools');
                }
            }
        } else { // After successful update
            setTool(prev => ({...prev, uuid: toolUuid})); // Ensure UUID is in state for edited tool
            // Potentially re-fetch tool data to ensure full consistency if backend modifies things on PUT
            // For now, we assume frontend state + separate code update is sufficient.
        }
      }
    } catch (err) {
      toast.error(`An unexpected error occurred: ${err.message}`);
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => navigate('/tools');
  const isFormDirty = JSON.stringify(tool) !== JSON.stringify(originalToolDataForCompare);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={handleBack} sx={{ mr: 1 }} aria-label="Back to tools list">
            <BackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {isEditing ? 'Edit Tool' : 'Create New Tool'}
          </Typography>
          {isEditing && tool.name && <Chip label={tool.name} sx={{ ml: 2 }} variant="outlined" />}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {isEditing && toolUuid && (
            <Button variant="outlined" startIcon={<PlayArrowIcon />} onClick={() => navigate(`/tools/${toolUuid}/test`)} disabled={saving || !toolUuid}>
              Test Tool
            </Button>
          )}
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit} disabled={saving || !isFormDirty}>
            {saving ? <CircularProgress size={24} color="inherit" /> : (isEditing ? 'Save Changes' : 'Create Tool')}
          </Button>
        </Box>
      </Box>
      {isFormDirty && !saving && (
        <Alert severity="info" sx={{ mb: 2 }}>You have unsaved changes.</Alert>
      )}

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
        <Tab label="Tool Details" id="tool-tab-0" aria-controls="tool-tabpanel-0" />
        <Tab label="Code Editor" id="tool-tab-1" aria-controls="tool-tabpanel-1" />
      </Tabs>

      <Box role="tabpanel" hidden={tabValue !== 0} id="tool-tabpanel-0" aria-labelledby="tool-tab-0">
        {tabValue === 0 && <ToolDetailsTab tool={tool} handleChange={handleChange} errors={errors} isEditing={isEditing} />}
      </Box>
      <Box role="tabpanel" hidden={tabValue !== 1} id="tool-tabpanel-1" aria-labelledby="tool-tab-1">
        {tabValue === 1 && (
          <ToolCodeTab
            toolCode={tool.code}
            initialToolCode={initialToolCodeForReset}
            onCodeChange={handleCodeChange}
            onResetCode={handleResetCode}
            toolName={tool.name}
            toolDescription={tool.description}
            toolDefaultParams={tool.default_params}
          />
        )}
      </Box>
    </Box>
  );
};

export default ToolEditor;